/**
 * Shared credential-shaped-string scrubber.
 *
 * Used by every capture module (console, error, performance, network) so a
 * single regression in any one path can't leak credentials a sibling path
 * already cleans. Patterns mirror the ones in the URL sanitizer's
 * value-shape detector — keep them in sync.
 */

export const SENSITIVE_TOKEN_PATTERNS: RegExp[] = [
  // Mhosaic feedback keys. Match ANY tail length — a backend error echo can
  // truncate the suffix (e.g. `sk_proj_***abc`) and the redaction must still
  // catch the prefix.
  /\b(?:sk|pk)_proj_[A-Za-z0-9_*-]+/g,
  // JWT shape (header.payload.signature).
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
  // "Bearer <token>" and "Authorization: …" headers.
  /\bBearer\s+[A-Za-z0-9._~+/=-]{16,}\b/g,
  /\bAuthorization\s*[:=]\s*["']?[A-Za-z0-9._~+/=-]{16,}["']?/gi,
  // GitHub PATs.
  /\bgh[pousr]_[A-Za-z0-9]{30,}\b/g,
  // Stripe live/test secret keys + webhook secrets.
  /\b(?:sk|pk|rk|whsec)_(?:live|test)_[A-Za-z0-9]{16,}\b/g,
  // AWS access key id.
  /\bAKIA[0-9A-Z]{12,}\b/g,
  // Slack tokens.
  /\bxox[abprso]-[A-Za-z0-9-]{12,}\b/g,
  // Google API keys (Maps, GCP, Firebase, etc.).
  /\bAIza[0-9A-Za-z_-]{35}\b/g,
  // OpenAI API keys (legacy, project-scoped, service-account).
  /\bsk-(?:proj-|svcacct-)?[A-Za-z0-9_-]{20,}\b/g,
  // Anthropic API keys.
  /\bsk-ant-(?:api03-)?[A-Za-z0-9_-]{40,}\b/g,
  // Twilio account / API SIDs (paired secret would also be redacted by the
  // Stripe-shape regex if it's prefixed sk_).
  /\bAC[a-f0-9]{32}\b/g,
  /\bSK[a-f0-9]{32}\b/g,
]

export function scrubCredentials(text: string): string {
  let out = text
  for (const re of SENSITIVE_TOKEN_PATTERNS) {
    out = out.replace(re, '[redacted-token]')
  }
  return out
}
